import type { PaymentAmountSelection } from "../../features/checkout/amountSelection";
import type { PendingPayment } from "../../features/checkout/checkoutTypes";
import { formatMoney } from "../../lib/format";
import type { PaymentSummary } from "../../features/checkout/payment";
import { calculateSurchargeAmount } from "../../features/checkout/surcharge";
import { SurchargeDisclosure } from "./SurchargeDisclosure";

type Props = {
  error: string | null;
  payment: PaymentSummary;
  paymentAmount: PaymentAmountSelection;
  pendingPayment: PendingPayment;
  submitting: boolean;
  onBack: () => void;
  onSubmit: () => void;
};

export function PaymentConfirmationStep({
  error,
  payment,
  paymentAmount,
  pendingPayment,
  submitting,
  onBack,
  onSubmit,
}: Props) {
  const previewSurcharge =
    pendingPayment.request.applySurcharge && pendingPayment.card.canSurcharge;
  const surchargeAmount =
    previewSurcharge && payment.enabled
      ? calculateSurchargeAmount(paymentAmount.amount, payment.surchargePercentage)
      : 0;
  const totalCharge = paymentAmount.amount + surchargeAmount;

  function submit(event: Event) {
    event.preventDefault();
    onSubmit();
  }

  return (
    <section class="payment-panel" aria-label="Payment">
      <form class="payment-confirmation" onSubmit={submit}>
        <div class="payment-step-summary">
          <span>Card</span>
          <strong>
            {pendingPayment.card.brand} ending in {pendingPayment.card.lastFour}
          </strong>
          <button class="link-action" type="button" onClick={onBack} disabled={submitting}>
            Change
          </button>
        </div>

        <dl class="payment-review">
          <div>
            <dt>Amount applied</dt>
            <dd>{formatMoney(paymentAmount.amount)}</dd>
          </div>
          {surchargeAmount > 0 && (
            <div>
              <dt>Surcharge</dt>
              <dd>{formatMoney(surchargeAmount)}</dd>
            </div>
          )}
          <div class="payment-review__total">
            <dt>Total charge</dt>
            <dd>{formatMoney(totalCharge)}</dd>
          </div>
        </dl>

        {payment.enabled &&
          payment.surchargePercentage != null &&
          previewSurcharge &&
          surchargeAmount > 0 && (
            <SurchargeDisclosure
              surchargePercentage={payment.surchargePercentage}
              billingZipCode={pendingPayment.card.zipCode}
            />
          )}

        {error && (
          <p class="payment-error" role="alert">
            {error}
          </p>
        )}

        <button class="primary-action" type="submit" disabled={submitting}>
          {submitting ? "Submitting..." : "Submit payment"}
        </button>
        <button class="secondary-action" type="button" onClick={onBack} disabled={submitting}>
          Back
        </button>
      </form>
    </section>
  );
}
